import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useSettings } from '@/contexts/SettingsContext';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { authApi } from '@/services/authApi';

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [logoError, setLogoError] = useState(false);
  const { user } = useAuth();
  const { getPrimaryColor, getFavicon, getLogo, getAuthPageLogo, getTitle } = useSettings();
  const navigate = useNavigate();

  // Helper function to darken a color
  const darkenColor = (color: string, percent: number): string => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
    const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
    const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Get primary color and calculate darker shades for gradient
  const primaryColor = getPrimaryColor();
  const faviconUrl = getFavicon();
  const logoUrl = getLogo();
  const authPageLogoUrl = getAuthPageLogo();
  const portalTitle = getTitle();
  const gradientColors = React.useMemo(() => {
    const darker = darkenColor(primaryColor, 25);
    const darkest = darkenColor(primaryColor, 45);
    return {
      start: primaryColor,
      end: darkest,
      mid: darker,
    };
  }, [primaryColor]);

  // Redirect to dashboard if user is already logged in
  React.useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!email) {
      setError('Email is required');
      return;
    }
    
    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    
    try {
      await authApi.forgotPassword(email);
      setIsSubmitted(true);
    } catch (error: any) {
      console.error('Forgot password failed:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to send reset email. Please try again.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-vh-100 position-relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)` }}>
        {/* Background Elements */}
        <div className="position-absolute top-0 start-0 w-100 h-100">
          <div className="position-absolute" style={{ top: '10%', left: '5%', width: '200px', height: '200px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '20px', transform: 'rotate(15deg)', opacity: 0.9 }}></div>
          <div className="position-absolute" style={{ top: '20%', right: '10%', width: '150px', height: '150px', background: `linear-gradient(135deg, ${gradientColors.end} 0%, ${gradientColors.mid} 100%)`, borderRadius: '50%', opacity: 0.9 }}></div>
        </div>

        <div className="container position-relative" style={{ zIndex: 10 }}>
          <div className="row justify-content-center min-vh-100 align-items-center">
            <div className="col-lg-10 col-xl-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="position-relative"
              >
                <div className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white" style={{ minHeight: '600px' }}>
                  {/* Left Side - Branding */}
                  <div className="col-lg-6 position-relative d-none d-lg-flex" style={{ background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)' }}>
                    <div className="position-relative p-3 p-md-4 p-lg-5 h-100 d-flex flex-column justify-content-center text-white" style={{ overflow: 'auto' }}>
                      <div className="text-center mb-4 mb-md-5">
                        <div className="d-flex align-items-center justify-content-center mx-auto mb-3 mb-md-4" style={{ maxWidth: '100%', padding: '0 1rem' }}>
                          {logoError ? (
                            <span className="fs-1 fw-bold text-white" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>{portalTitle}</span>
                          ) : (
                            <img 
                              src={authPageLogoUrl || faviconUrl || '/scholar_vest_logo.png'} 
                              alt={`${portalTitle} Agent Portal Logo`} 
                              style={{ 
                                height: "auto",
                                width: "auto",
                                maxHeight: "clamp(40px, 8vw, 80px)",
                                maxWidth: "100%",
                                objectFit: "contain",
                                objectPosition: "center"
                              }}
                              onError={() => setLogoError(true)}
                            />
                          )}
                        </div>
                        <p className="fs-5 text-white text-opacity-90">Agent Portal</p>
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Success Message */}
                  <div className="col-lg-6 p-3 p-md-4 p-lg-5 d-flex flex-column justify-content-center" style={{ overflow: 'auto' }}>
                    <div className="w-100 text-center">
                      {/* Success Icon */}
                      <div className="mb-4">
                        <div 
                          className="rounded-circle d-inline-flex align-items-center justify-content-center"
                          style={{ 
                            width: '80px', 
                            height: '80px', 
                            backgroundColor: '#10b981',
                            color: 'white'
                          }}
                        >
                          <CheckCircle size={40} />
                        </div>
                      </div>

                      {/* Success Title */}
                      <h2 className="fs-2 fw-bold text-dark mb-3">
                        Check Your Email
                      </h2>

                      {/* Success Message */}
                      <p className="text-muted mb-4" style={{ fontSize: '16px' }}>
                        We've sent password reset instructions to <strong>{email}</strong>
                      </p>

                      {/* Additional Info */}
                      <div className="alert alert-info" role="alert" style={{ fontSize: '14px' }}>
                        <small>
                          <strong>Didn't receive the email?</strong> Check your spam folder or try again.
                        </small>
                      </div>

                      {/* Back to Login */}
                      <div className="mt-4">
                        <Link 
                          to="/login" 
                          className="d-inline-flex align-items-center text-decoration-none"
                          style={{ color: primaryColor, fontSize: '16px', fontWeight: '500' }}
                        >
                          <ArrowLeft size={20} className="me-2" />
                          Back to Login
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 position-relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)` }}>
      {/* Background Elements */}
      <div className="position-absolute top-0 start-0 w-100 h-100">
        <div className="position-absolute" style={{ top: '10%', left: '5%', width: '200px', height: '200px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '20px', transform: 'rotate(15deg)', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ top: '20%', right: '10%', width: '150px', height: '150px', background: `linear-gradient(135deg, ${gradientColors.end} 0%, ${gradientColors.mid} 100%)`, borderRadius: '50%', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ bottom: '20%', left: '15%', width: '100px', height: '100px', background: `linear-gradient(135deg, ${gradientColors.start} 0%, ${gradientColors.end} 100%)`, borderRadius: '15px', transform: 'rotate(-30deg)', opacity: 0.9 }}></div>
        <div className="position-absolute" style={{ bottom: '30%', right: '20%', width: '120px', height: '120px', background: `linear-gradient(135deg, ${gradientColors.mid} 0%, ${gradientColors.end} 100%)`, borderRadius: '25px', transform: 'rotate(45deg)', opacity: 0.9 }}></div>
      </div>

      <div className="container position-relative" style={{ zIndex: 10 }}>
        <div className="row justify-content-center min-vh-100 align-items-center">
          <div className="col-lg-10 col-xl-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="position-relative"
            >
              {/* Main Content Container */}
              <div className="row g-0 shadow-lg rounded-4 overflow-hidden bg-white" style={{ minHeight: '600px' }}>
                {/* Left Side - Branding */}
                <div className="col-lg-6 position-relative d-none d-lg-flex" style={{ background: 'linear-gradient(135deg, var(--primary, #1e40af) 0%, var(--primary-dark, #1e3a8a) 100%)' }}>
                  <div className="position-relative p-3 p-md-4 p-lg-5 h-100 d-flex flex-column justify-content-center text-white" style={{ overflow: 'auto' }}>
                    <div className="text-center mb-4 mb-md-5">
                      {/* Logo */}
                      <div className="d-flex align-items-center justify-content-center mx-auto mb-3 mb-md-4" style={{ maxWidth: '100%', padding: '0 1rem' }}>
                        {logoError ? (
                          <span className="fs-1 fw-bold text-white" style={{ fontSize: 'clamp(1.5rem, 4vw, 3rem)' }}>{portalTitle}</span>
                        ) : (
                          <img 
                            src={authPageLogoUrl || faviconUrl || '/scholar_vest_logo.png'} 
                            alt={`${portalTitle} Agent Portal Logo`} 
                            style={{ 
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(40px, 8vw, 80px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      <p className="fs-5 text-white text-opacity-90">Agent Portal</p>
                      <h6 className="fs-2 fw-bold mb-3">Reset Your Password</h6>
                    </div>
                    
                    <div className="text-center">
                      <p className="fs-5 text-white text-opacity-90 mb-0">
                        Enter your email address and we'll send you a link to reset your password
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Side - Form */}
                <div className="col-lg-6 p-3 p-md-4 p-lg-5 d-flex flex-column justify-content-center" style={{ overflow: 'auto' }}>
                  <div className="w-100">
                    {/* Logo for mobile */}
                    <div className="text-center mb-4 d-lg-none" style={{ padding: '0 0.5rem' }}>
                      <div className="d-inline-flex align-items-center justify-content-center mb-3" style={{ maxWidth: '100%' }}>
                        {logoError ? (
                          <span className="fs-2 fw-bold text-primary" style={{ fontSize: 'clamp(1.25rem, 5vw, 2rem)' }}>{portalTitle}</span>
                        ) : (
                          <img 
                            src={authPageLogoUrl || logoUrl || '/scholar_vest_logo_full.png'} 
                            alt={`${portalTitle} Agent Portal Logo`} 
                            style={{ 
                              height: "auto",
                              width: "auto",
                              maxHeight: "clamp(40px, 10vw, 70px)",
                              maxWidth: "100%",
                              objectFit: "contain",
                              objectPosition: "center"
                            }}
                            onError={() => setLogoError(true)}
                          />
                        )}
                      </div>
                      <h2 className="fs-3 fw-bold text-dark mb-1">Agent Portal</h2>
                    </div>

                    <div className="text-center mb-4">
                      <h3 className="fs-2 fw-bold text-dark mb-2">Forgot Password</h3>
                      <p className="text-muted">No worries, we'll send you reset instructions</p>
                    </div>

                    {error && (
                      <div className="alert alert-danger d-flex align-items-center mb-3" role="alert">
                        <AlertCircle size={18} className="me-2" />
                        <small>{error}</small>
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      {/* Email */}
                      <div className="mb-4">
                        <label className="form-label fw-semibold text-dark">Email Address</label>
                        <div className="input-group">
                          <span className="input-group-text bg-light border-end-0">
                            <Mail size={18} className="text-muted" />
                          </span>
                          <input
                            type="email"
                            className="form-control border-start-0"
                            name="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            required
                          />
                        </div>
                      </div>

                      {/* Submit Button */}
                      <button
                        type="submit"
                        className="btn btn-primary btn-lg w-100 mb-3 d-flex align-items-center justify-content-center gap-2"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <div className="spinner-border spinner-border-sm" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Sending...
                          </>
                        ) : (
                          <>
                            Send Reset Link
                            <ArrowLeft size={18} />
                          </>
                        )}
                      </button>

                      {/* Back to Login Link */}
                      <div className="text-center">
                        <Link 
                          to="/login" 
                          className="d-inline-flex align-items-center text-decoration-none"
                          style={{ color: primaryColor, fontSize: '16px', fontWeight: '500' }}
                        >
                          <ArrowLeft size={18} className="me-2" />
                          Back to Login
                        </Link>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

